import { AggregationOperation, CalendarUnit, Chart, ChartType } from '@doss/prisma/types'
import _ from 'lodash'
import * as yup from 'yup'
import { DossError } from '../doss-error.js'
import { ChartSubType } from '../prisma-duplicate-types.js'
import { dateTimeOperandSchema } from './table-column-filter-operand.js'

export enum PivotDimensionType {
  ROW = 'ROW',
  COLUMN = 'COLUMN',
}

export const PIVOT_TOTAL_HEADER_KEY = 'total'
export const PIVOT_ROW_TOTAL_HEADER_KEY = '__total__'

// This is a special value that is used to represent a missing field value for a pivot table
// It is used to ensure that dimensions are still processed even if the field is missing
// from the data
export const MISSING_VALUE_PLACEHOLDER = '(blank)'

const groupByMetadataSchema = yup
  .object({
    interval: yup.number().min(1).notRequired(),
    intervalCalendarUnit: yup.string().oneOf(Object.values(CalendarUnit)).notRequired(),
    offset: yup.number().notRequired(),
    offsetCalendarUnit: yup
      .string()
      .oneOf(Object.values(CalendarUnit))
      .when('offset', ([offset], schema) => (offset ? schema.required() : schema.notRequired())),
    stringBuckets: yup.array(yup.string().required()).notRequired(),
    bounds: yup.object({
      lower: yup.lazy((value) => {
        if (_.isEmpty(value)) {
          return yup.number().notRequired()
        }
        return typeof value === 'number'
          ? yup.number().required()
          : dateTimeOperandSchema.required()
      }),
      upper: yup.lazy((value) => {
        if (_.isEmpty(value)) {
          return yup.number().notRequired()
        }
        return typeof value === 'number'
          ? yup.number().required()
          : dateTimeOperandSchema.required()
      }),
    }),
  })
  .strict()

// Pivot table group by metadata schema is the same as the group by metadata schema with three
// additional fields
const pivotTableGroupByMetadataSchema = groupByMetadataSchema.clone().shape({
  dimensionType: yup.string().oneOf(Object.values(PivotDimensionType)).required(),
  order: yup.number().required(),
  showTotals: yup.boolean().required(),
})

export type GroupByMetadataData = yup.InferType<typeof groupByMetadataSchema>
export type PivotTableGroupByMetadataData = yup.InferType<typeof pivotTableGroupByMetadataSchema>

export function checkGroupByMetadata(data: unknown): data is GroupByMetadataData {
  if (typeof data === 'undefined') {
    return false
  }
  return groupByMetadataSchema.isValidSync(data)
}

export function checkPivotTableGroupByMetadata(
  data: unknown,
): data is PivotTableGroupByMetadataData {
  if (typeof data === 'undefined') {
    return false
  }
  return pivotTableGroupByMetadataSchema.isValidSync(data)
}

const chartStylingSchema = yup
  .object({
    subType: yup.string().required().oneOf(Object.values(ChartSubType)),
  })
  .strict()

const pieChartStylingSchema = chartStylingSchema
  .shape({
    subType: yup.string().required().oneOf([ChartSubType.PIE]),
  })
  .strict()

const donutChartStylingSchema = chartStylingSchema
  .shape({
    subType: yup.string().required().oneOf([ChartSubType.DONUT]),
  })
  .strict()

const gaugeChartStylingSchema = chartStylingSchema
  .shape({
    subType: yup.string().required().oneOf([ChartSubType.GAUGE]),
  })
  .strict()

const metricChartStylingSchema = chartStylingSchema
  .shape({
    subType: yup.string().required().oneOf([ChartSubType.METRIC]),
  })
  .strict()

export const lineChartStylingSchema = chartStylingSchema
  .shape({
    subType: yup.string().required().oneOf([ChartSubType.LINE]),
  })
  .strict()

const areaChartStylingSchema = chartStylingSchema
  .shape({
    subType: yup.string().required().oneOf([ChartSubType.AREA]),
  })
  .strict()

const scatterplotChartStylingSchema = chartStylingSchema
  .shape({
    subType: yup.string().required().oneOf([ChartSubType.SCATTERPLOT]),
  })
  .strict()

const barChartStylingSchema = chartStylingSchema
  .shape({
    subType: yup.string().required().oneOf([ChartSubType.BAR]),
  })
  .strict()

const columnChartStylingSchema = chartStylingSchema
  .shape({
    subType: yup.string().required().oneOf([ChartSubType.COLUMN]),
  })
  .strict()

const histogramChartStylingSchema = chartStylingSchema
  .shape({
    subType: yup.string().required().oneOf([ChartSubType.HISTOGRAM]),
  })
  .strict()

const pivotTableChartStylingSchema = chartStylingSchema
  .shape({
    subType: yup.string().required().oneOf([ChartSubType.PIVOT_TABLE]),
  })
  .strict()

export type ChartStylingSchemaData = yup.InferType<typeof chartStylingSchema>
export type PieChartStylingSchemaData = yup.InferType<typeof pieChartStylingSchema>
export type DonutChartStylingSchemaData = yup.InferType<typeof donutChartStylingSchema>
export type MetricChartStylingSchemaData = yup.InferType<typeof metricChartStylingSchema>
export type GaugeChartStylingSchemaData = yup.InferType<typeof gaugeChartStylingSchema>
export type LineChartStylingSchemaData = yup.InferType<typeof lineChartStylingSchema>
export type AreaChartStylingSchemaData = yup.InferType<typeof areaChartStylingSchema>
export type ScatterplotChartStylingSchemaData = yup.InferType<typeof scatterplotChartStylingSchema>
export type BarChartStylingSchemaData = yup.InferType<typeof barChartStylingSchema>
export type ColumnChartStylingSchemaData = yup.InferType<typeof columnChartStylingSchema>
export type HistogramChartStylingSchemaData = yup.InferType<typeof histogramChartStylingSchema>
export type PivotTableChartStylingSchemaData = yup.InferType<typeof pivotTableChartStylingSchema>

export type ChartTypeStylingSchemaData =
  | PieChartStylingSchemaData
  | DonutChartStylingSchemaData
  | MetricChartStylingSchemaData
  | GaugeChartStylingSchemaData
  | LineChartStylingSchemaData
  | AreaChartStylingSchemaData
  | ScatterplotChartStylingSchemaData
  | BarChartStylingSchemaData
  | ColumnChartStylingSchemaData
  | HistogramChartStylingSchemaData
  | PivotTableChartStylingSchemaData

export function getChartSubType(chart: Chart): ChartSubType {
  switch (chart.chartType) {
    case ChartType.PIE:
      if (pieChartStylingSchema.isValidSync(chart.styling)) {
        return ChartSubType.PIE
      }
      if (donutChartStylingSchema.isValidSync(chart.styling)) {
        return ChartSubType.DONUT
      }
      throw new DossError('Invalid chart styling for chart base type PIE', [])
    case ChartType.LINE:
      if (lineChartStylingSchema.isValidSync(chart.styling)) {
        return ChartSubType.LINE
      }
      if (areaChartStylingSchema.isValidSync(chart.styling)) {
        return ChartSubType.AREA
      }
      throw new DossError('Invalid chart styling for chart base type LINE', [])
    case ChartType.SCATTERPLOT:
      if (scatterplotChartStylingSchema.isValidSync(chart.styling)) {
        return ChartSubType.SCATTERPLOT
      }
      throw new DossError('Invalid chart styling for chart base', [])
    case ChartType.BAR:
      if (barChartStylingSchema.isValidSync(chart.styling)) {
        return ChartSubType.BAR
      }
      if (columnChartStylingSchema.isValidSync(chart.styling)) {
        return ChartSubType.COLUMN
      }
      if (histogramChartStylingSchema.isValidSync(chart.styling)) {
        return ChartSubType.HISTOGRAM
      }
      throw new DossError('Invalid chart styling for chart base type BAR', [])
    case ChartType.METRIC:
      if (gaugeChartStylingSchema.isValidSync(chart.styling)) {
        return ChartSubType.GAUGE
      }
      return ChartSubType.METRIC
    case ChartType.PIVOT_TABLE:
      if (pivotTableChartStylingSchema.isValidSync(chart.styling)) {
        return ChartSubType.PIVOT_TABLE
      }
      throw new DossError('Invalid chart styling for chart base type PIVOT_TABLE', [])
  }
}

// Schema for a single PivotTableCell
const pivotTableCellSchema = yup.object().shape({
  value: yup.number().required(),
  columnId: yup.string().required(),
  rowKeys: yup.array().of(yup.string().required()).required(),
  columnKeys: yup.array().of(yup.string().required()).required(),
  percentOfRow: yup.number().required(),
  percentOfColumn: yup.number().required(),
  percentOfGrandTotal: yup.number().required(),
  isRowTotal: yup.boolean().required(),
  isColumnTotal: yup.boolean().required(),
  aggregationOperation: yup.string().oneOf(Object.values(AggregationOperation)),
})

export type PivotTableCell = yup.InferType<typeof pivotTableCellSchema>
